import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { onboarding } from '../services/api';
import { validateLBTCAddress } from '../utils/validation';
import { InlineError } from '../components/common/ErrorMessage';

const STEPS = ['Welcome', 'Wallet Address', 'Contracts Created', 'Integration'];

function StepIndicator({ current }) {
  return (
    <div className="flex items-center justify-center gap-0 mb-8">
      {STEPS.map((label, i) => (
        <React.Fragment key={label}>
          <div className="flex flex-col items-center gap-1.5">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-all duration-300 ${
                i < current
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : i === current
                  ? 'bg-blue-600/20 border-blue-500 text-blue-400'
                  : 'bg-gray-800 border-gray-700 text-gray-500'
              }`}
            >
              {i < current ? (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                i + 1
              )}
            </div>
            <span className={`text-xs font-medium whitespace-nowrap ${i === current ? 'text-blue-400' : 'text-gray-500'}`}>
              {label}
            </span>
          </div>
          {i < STEPS.length - 1 && (
            <div className={`h-px w-12 mb-5 mx-1 transition-colors duration-300 ${i < current ? 'bg-blue-600' : 'bg-gray-700'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function AddressDisplay({ label, address, accent = false }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(address).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className={`rounded-lg border p-3 ${accent ? 'bg-blue-500/5 border-blue-500/20' : 'bg-gray-800 border-gray-700'}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-gray-400">{label}</span>
        <button
          onClick={handleCopy}
          className="text-xs text-gray-500 hover:text-gray-200 transition-colors flex items-center gap-1"
        >
          {copied ? (
            <><svg className="w-3.5 h-3.5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg><span className="text-green-400">Copied</span></>
          ) : (
            <><svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>Copy</>
          )}
        </button>
      </div>
      <code className="text-xs font-mono text-gray-200 break-all leading-relaxed">{address}</code>
    </div>
  );
}

export default function Onboarding() {
  const { merchant, updateMerchant } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [walletAddress, setWalletAddress] = useState('');
  const [walletError, setWalletError] = useState('');
  const [loading, setLoading] = useState(false);
  const [contracts, setContracts] = useState(null);

  const handleWalletSubmit = async (e) => {
    e.preventDefault();
    const err = validateLBTCAddress(walletAddress);
    if (err) {
      setWalletError(err);
      return;
    }

    setLoading(true);
    setWalletError('');
    try {
      const result = await onboarding.submit({ wallet_address: walletAddress });
      // Backend returns { merchant } — store the updated merchant object
      setContracts(result.merchant);
      updateMerchant(result.merchant);
      setStep(2);
    } catch (err) {
      setWalletError(err.message || 'Failed to create contracts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = () => {
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12">
      {/* Logo */}
      <div className="flex items-center gap-2 mb-10">
        <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z" />
          </svg>
        </div>
        <span className="text-lg font-bold text-white">MintBridge</span>
      </div>

      <div className="w-full max-w-lg">
        <StepIndicator current={step} />

        {/* Step 0: Welcome */}
        {step === 0 && (
          <div className="card text-center">
            <div className="w-16 h-16 rounded-2xl bg-blue-600/15 border border-blue-500/20 flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-white mb-3">
              Welcome, {merchant?.name || 'Merchant'}!
            </h1>
            <p className="text-gray-400 mb-6 leading-relaxed">
              Let's get your Liquid Bitcoin settlement set up. This takes about 2 minutes.
            </p>
            <div className="text-left space-y-3 mb-8">
              {[
                { icon: '1', text: 'Provide your Liquid Bitcoin wallet address' },
                { icon: '2', text: 'We compile three Simplicity contracts on your behalf' },
                { icon: '3', text: 'Add the MintBridge checkout button to your site' },
              ].map(item => (
                <div key={item.icon} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-blue-600/15 border border-blue-500/20 flex items-center justify-center flex-shrink-0 text-xs text-blue-400 font-bold mt-0.5">
                    {item.icon}
                  </div>
                  <p className="text-sm text-gray-300">{item.text}</p>
                </div>
              ))}
            </div>
            <button onClick={() => setStep(1)} className="btn-primary w-full">
              Get Started
            </button>
          </div>
        )}

        {/* Step 1: Wallet Address */}
        {step === 1 && (
          <div className="card">
            <h2 className="text-xl font-bold text-white mb-2">Your Liquid Bitcoin Wallet</h2>
            <p className="text-sm text-gray-400 mb-6 leading-relaxed">
              Enter your Liquid Bitcoin wallet address. This is where 95% of every payment will be sent directly.
            </p>

            <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-3 mb-6">
              <div className="flex gap-2">
                <svg className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="text-xs text-blue-300">
                  Accepts testnet (<code className="font-mono">tex1q...</code> or <code className="font-mono">tex1p...</code>) and mainnet (<code className="font-mono">ex1q...</code> or <code className="font-mono">ex1p...</code>) bech32 addresses.
                </div>
              </div>
            </div>

            <form onSubmit={handleWalletSubmit} noValidate>
              <div className="mb-4">
                <label className="label" htmlFor="wallet">Wallet Address</label>
                <input
                  id="wallet"
                  type="text"
                  className="input-field font-mono text-xs"
                  placeholder="tex1q..."
                  value={walletAddress}
                  onChange={e => {
                    setWalletAddress(e.target.value);
                    if (walletError) setWalletError('');
                  }}
                  spellCheck={false}
                />
                <InlineError message={walletError} />
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => setStep(0)} className="btn-secondary flex-1">
                  Back
                </button>
                <button type="submit" disabled={loading} className="btn-primary flex-1">
                  {loading ? 'Creating contracts...' : 'Continue'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Step 2: Contracts pending or deployed */}
        {step === 2 && contracts && (
          <div className="card">
            {contracts.relay_contract_addr ? (
              <>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-green-500/15 border border-green-500/20 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white">Contracts Deployed!</h2>
                    <p className="text-sm text-green-400">Your Simplicity contracts are live on the Liquid Network</p>
                  </div>
                </div>
                <div className="space-y-3 mb-6">
                  <AddressDisplay label="Your Wallet (Destination)" address={walletAddress} accent />
                  <AddressDisplay label="Relay Contract" address={contracts.relay_contract_addr} />
                  <AddressDisplay label="Customer Covenant" address={contracts.customer_covenant_addr} />
                  <AddressDisplay label="Merchant Escrow" address={contracts.escrow_contract_addr} />
                </div>
                <div className="bg-gray-800 rounded-lg p-3 mb-6 text-xs text-gray-400 leading-relaxed">
                  <strong className="text-gray-200">How it works:</strong> Customers pay through the relay contract.
                  95% routes directly to your wallet, 5% to your escrow contract.
                  After 10 Liquid confirmations, escrow is released.
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-yellow-500/15 border border-yellow-500/20 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-yellow-400 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white">Setting Up Your Contracts</h2>
                    <p className="text-sm text-yellow-400">Compiling your Simplicity contracts — this takes a moment</p>
                  </div>
                </div>
                <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-lg p-4 mb-6 text-sm text-yellow-300 leading-relaxed">
                  Your wallet address has been saved. We're compiling three Simplicity smart contracts on the Liquid Network for your account. This usually completes within a few minutes.
                  <br /><br />
                  You can go to the dashboard now — your contract addresses will appear in Settings once compilation is complete.
                </div>
              </>
            )}
            <button onClick={() => setStep(3)} className="btn-primary w-full">
              Continue to Integration
            </button>
          </div>
        )}

        {/* Step 3: Integration */}
        {step === 3 && (
          <div className="card">
            <h2 className="text-xl font-bold text-white mb-2">Add Checkout to Your Site</h2>
            <p className="text-sm text-gray-400 mb-6">
              Drop this snippet anywhere on your site to add the MintBridge checkout button.
            </p>

            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-400">HTML snippet</span>
              </div>
              <div className="code-block text-xs leading-relaxed">
                {`<!-- MintBridge Checkout Button -->
<script src="https://merchant.mintbridge.cloud/sdk.js"></script>
<button
  data-mintbridge-merchant="${contracts?.relay_contract_addr?.slice(0, 20) || contracts?.merchant_dest_addr?.slice(0, 20) || 'tex1p...'}"
  data-amount="1000"
  data-currency="USD"
  class="mintbridge-checkout-btn"
>
  Pay with MintBridge
</button>`}
              </div>
            </div>

            <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-lg p-3 mb-6">
              <div className="flex gap-2">
                <svg className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <p className="text-xs text-yellow-300">
                  You're currently on <strong>testnet</strong>. Payments will use test cards only. Switch to mainnet before going live.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <button type="button" onClick={() => setStep(2)} className="btn-secondary flex-1">
                Back
              </button>
              <button onClick={handleComplete} className="btn-primary flex-1">
                Go to Dashboard
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
